import React from "react";
import { motion } from "framer-motion";
import { fmtMoney, fmtInvestment } from "@/lib/api";
import {
  Calculator,
  ArrowDown,
  Gauge,
  DollarSign,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  ListChecks,
} from "lucide-react";

const Card = ({ children, accent, testId, className = "" }) => (
  <div
    className={`rounded-xl border p-5 sm:p-6 ${accent ? "border-[#1E6BFF]/25" : "border-white/10"} ${className}`}
    style={{ backgroundColor: accent ? "#101a33" : "#0F1424" }}
    data-testid={testId}
  >
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-2 border-b border-white/5 last:border-0">
    <span className="text-white/60 text-sm">{label}</span>
    <span className="text-white font-semibold tabular-nums text-right">{value}</span>
  </div>
);

const Arrow = () => (
  <div className="flex justify-center py-1 text-white/30">
    <ArrowDown size={16} />
  </div>
);

const pct = (n) => `${Math.round((n || 0) * 100)}%`;

export const CapacityAnalysis = ({ result, showPricing = false }) => {
  const wc = result?.workforce_calculation;
  const conf = result?.recommendation_confidence;
  const rs = result?.recommendation_summary;
  if (!wc) return null;

  const reservePct = Math.round((wc.reserve_target_pct || 0) * 100);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="capacity-analysis"
    >
      <div className="flex items-center gap-2 mb-1">
        <Calculator size={20} className="text-[#66B2FF]" />
        <h2 className="text-2xl font-bold">Workforce Capacity Analysis</h2>
      </div>
      <p className="text-white/60 mb-6">
        How we calculated your recommendation &mdash; every number, shown transparently.
      </p>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Estimated workload */}
        <Card testId="capacity-workload">
          <h3 className="text-white font-semibold mb-3">Estimated administrative workload</h3>
          <Row label="Selected workload range" value={wc.weekly_range} />
          <Row label="Planning midpoint used" value={`${wc.weekly_midpoint} hrs/week`} />
          <Arrow />
          <Row
            label={`Monthly workload (${wc.weekly_midpoint} × ${wc.weeks_per_month} weeks)`}
            value={`${wc.base_monthly_hours} hrs/month`}
          />
        </Card>

        {/* Operational adjustments */}
        <Card testId="capacity-adjustments">
          <h3 className="text-white font-semibold mb-3">Operational adjustments</h3>
          {wc.modifiers.length === 0 ? (
            <p className="text-white/55 text-sm">No additional workload drivers identified.</p>
          ) : (
            <div className="space-y-2">
              {wc.modifiers.map((m, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-white/75">{m.factor}</span>
                  <span className="text-[#66B2FF] font-semibold tabular-nums">+{pct(m.impact)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-white/10">
            <Row label="Base workload" value={`${wc.base_monthly_hours} hrs`} />
            <Arrow />
            <Row
              label={`Adjusted workload (+${pct(wc.total_modifier_pct)})`}
              value={`${wc.adjusted_monthly_hours} hrs/month`}
            />
          </div>
        </Card>
      </div>

      {/* Workload allocation by role */}
      <Card testId="capacity-allocation" className="mt-4">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks size={18} className="text-[#66B2FF]" />
          <h3 className="text-white font-semibold">Workload allocation by role</h3>
        </div>
        <div className="rounded-lg border border-white/10 divide-y divide-white/5" style={{ backgroundColor: "#0A0F1E" }}>
          {wc.role_allocations.map((r, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-white/80 text-sm">
                {r.name}{r.seats > 1 ? ` (${r.seats} seats)` : ""}
              </span>
              <span className="text-white font-semibold tabular-nums text-sm">{r.allocated_hours} hrs</span>
            </div>
          ))}
          <div className="flex items-center justify-between px-4 py-2.5 bg-white/5">
            <span className="text-white/70 text-sm font-medium">Total allocated workload</span>
            <span className="text-white font-bold tabular-nums text-sm">{wc.adjusted_monthly_hours} hrs</span>
          </div>
        </div>
      </Card>

      {/* Capacity calculation + reserve */}
      <Card testId="capacity-calculation" className="mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Gauge size={18} className="text-[#66B2FF]" />
          <h3 className="text-white font-semibold">Workforce capacity calculation</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8">
          <div>
            <Row
              label="Productive hours / full-time employee"
              value={`${wc.productive_hours_per_fte} hrs/month`}
            />
            <Row label="Required capacity" value={`${wc.adjusted_monthly_hours} hrs/month`} />
            <Row label="Calculated staffing requirement" value={`${wc.required_fte} FTE`} />
          </div>
          <div>
            <Row label="Recommended staffing" value={`${wc.recommended_seats} full-time`} />
            <Row label="Capacity provided" value={`${wc.capacity_provided} hrs/month`} />
            <Row label="Expected utilization" value={`${wc.utilization_percent}%`} />
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <div className="rounded-lg bg-white/5 border border-white/10 p-3">
            <div className="text-white/50 text-xs">Planned reserve target</div>
            <div className="text-white font-semibold tabular-nums">{reservePct}%</div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-3">
            <div className="text-white/50 text-xs">Remaining reserve</div>
            <div className="text-white font-semibold tabular-nums">{wc.reserve_capacity_hours} hrs</div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-3">
            <div className="text-white/50 text-xs">Expected utilization</div>
            <div className="text-white font-semibold tabular-nums">{wc.utilization_percent}%</div>
          </div>
        </div>
        <p className="text-white/55 text-sm mt-3">
          The reserve buffer covers reasonable peak periods, PTO and coverage, and near-term growth,
          so the team is not run at unsustainable 100% utilization.
        </p>
      </Card>

      {/* Financial analysis (only when pricing is enabled) */}
      {showPricing && (
      <Card testId="capacity-financials" className="mt-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign size={18} className="text-[#66B2FF]" />
          <h3 className="text-white font-semibold">Financial analysis</h3>
        </div>
        <div className="rounded-lg border border-white/10 divide-y divide-white/5" style={{ backgroundColor: "#0A0F1E" }}>
          {[
            ["Estimated monthly workload", `${wc.adjusted_monthly_hours} hrs`],
            ["Dedicated team capacity provided", `${wc.capacity_provided} hrs`],
            ["Monthly investment", fmtInvestment(result.investment_low, result.investment_high)],
            ["Estimated in-house equivalent", `${fmtMoney(wc.inhouse_equivalent)}/month`],
            ["Estimated monthly savings", `${fmtMoney(wc.monthly_savings)}/month`],
            ["Annual savings", fmtMoney(wc.annual_savings)],
            ["Return on investment (ROI)", `${wc.roi_percent}%`],
          ].map(([k, v], i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-white/70 text-sm">{k}</span>
              <span className="text-white font-semibold tabular-nums text-sm">{v}</span>
            </div>
          ))}
        </div>
      </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        {/* AI recommendation summary */}
        {rs ? (
          <Card testId="capacity-recommendation-summary">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-[#66B2FF]" />
              <h3 className="text-white font-semibold">AI recommendation summary</h3>
            </div>
            <p className="text-white/80 text-sm leading-relaxed">{rs.narrative}</p>
            {rs.responsibilities?.length ? (
              <ul className="mt-3 space-y-1.5">
                {rs.responsibilities.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
                    <CheckCircle2 size={16} className="text-[#66B2FF] shrink-0 mt-0.5" />
                    {r}
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
        ) : null}

        {/* AI confidence */}
        {conf ? (
          <Card testId="capacity-confidence" accent>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={18} className="text-[#66B2FF]" />
              <h3 className="text-white font-semibold">AI confidence</h3>
            </div>
            <div className="flex items-end gap-3">
              <div className="text-4xl font-bold text-[#66B2FF] tabular-nums" data-testid="confidence-score">
                {conf.score}%
              </div>
              <div className="text-white/70 mb-1.5">{conf.label}</div>
            </div>
            <div className="text-white/60 text-sm mt-3">Based on:</div>
            <ul className="mt-2 space-y-1.5">
              {(conf.reasons || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                  <CheckCircle2 size={15} className="text-[#66B2FF] shrink-0 mt-0.5" />
                  {r}
                </li>
              ))}
            </ul>
            {conf.mode === "consultation" ? (
              <div
                className="mt-4 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-200"
                data-testid="confidence-consultation-note"
              >
                We recommend a quick consultation to confirm the best-fit team before treating this
                plan as definitive.
              </div>
            ) : null}
          </Card>
        ) : null}
      </div>
    </motion.section>
  );
};

export default CapacityAnalysis;
